import type { ComponentProps } from 'react';
import {
  Accordion,
  AccordionContent,
  AccordionTitle,
  Box,
  Icon,
  InlineStack,
  PanelSection,
  Strong,
  Table,
  TableTbody,
  TableTd,
  TableTh,
  TableThead,
  TableTr,
  Text,
  Truncated,
} from '@customerio/pluma-components/react';
import type { IconName } from '@customerio/pluma-icons';
import plur from 'plur';
import { AppErrorBoundary } from '@/components/app-error-boundary';
import { AccountFeatureFlag, useFeatureFlag } from '@/services/auth/feature-flags';
import { useAppsOptional } from '@/services/db/app/queries';
import { useCustomer } from '@/services/db/customer/queries';
import { customerDevices } from '@/services/db/customer/utils';
import type { Delivery } from '@/services/db/delivery/entity';
import { getDeliveryState, getDeviceStatuses, type PushDeliveryMetaReason } from '@/services/db/delivery/utils';
import { formatError } from '@/utils/format-error';
import { capitalize, isEmptyString } from '@/utils/string';
import {
  draftedRecipientDevices,
  groupDeviceStatuses,
  type PushDeliveryGroup,
  type PushDeliveryGroupType,
} from '../utils/push-results';

type TextColor = ComponentProps<typeof Text>['color'];

/**
 * Per-group text colors (Ember `TYPE_COLORS`): `label` tints the "Sent"/"Failed"
 * heading word, `message` tints the per-device failure reason.
 */
const TYPE_COLORS: Record<PushDeliveryGroupType, { label?: TextColor; message?: TextColor }> = {
  drafted: { label: 'caution' },
  sent: { label: 'success', message: 'success' },
  bounced: { message: 'caution' },
  suppressed: { message: 'caution' },
  failed: { label: 'critical', message: 'critical' },
  undeliverable: {},
};

const PLATFORM_ICONS: Record<string, IconName> = { web: 'desktop', ios: 'apple', android: 'android' };

function platformIcon(platform: string): IconName {
  return PLATFORM_ICONS[platform] ?? 'desktop';
}

type PushActionResultsProps = { delivery: Delivery };

/** Cross-cutting single-device options: the `multi_app` flag + the delivery's resolved app name. */
type SingleDeviceOptions = { enabled: boolean; appName: string | undefined };

/**
 * Push device-status results — the sent / undeliverable / bounced / suppressed /
 * failed accordions above the payload preview, ported from the Ember
 * `push-action-results` component. A drafted push shows its target devices in a
 * single `drafted` group instead (always present, even when empty — Ember never
 * filters it or falls back to the status groups). The first group is expanded by
 * default.
 *
 * Behind the `multi_app` flag, a group with a single device renders inline in the
 * header (with the app name) rather than as an accordion (Ember `singleDevice`).
 */
export function PushActionResults({ delivery }: PushActionResultsProps) {
  const multiApp = useFeatureFlag(AccountFeatureFlag.MultiApp);

  if (getDeliveryState(delivery) === 'drafted') {
    return <DraftedPushResults delivery={delivery} multiApp={multiApp} />;
  }

  return (
    <PushGroups groups={groupDeviceStatuses(getDeviceStatuses(delivery))} delivery={delivery} multiApp={multiApp} />
  );
}

type DraftedPushResultsProps = { delivery: Delivery; multiApp: boolean };

/**
 * A drafted push always shows one `drafted` group of its target devices (Ember
 * `deliveryGroups` drafted branch). With a customer it lists `recipientDevices`;
 * without one the group is simply empty — no customer read, and never the status path.
 */
function DraftedPushResults({ delivery, multiApp }: DraftedPushResultsProps) {
  const customerId = delivery.customer_id;
  const emptyGroup = <PushGroups groups={[{ type: 'drafted', devices: [] }]} delivery={delivery} multiApp={multiApp} />;
  if (isEmptyString(customerId)) {
    return emptyGroup;
  }

  // A deleted or inaccessible profile makes the (suspense) device read reject; degrade to
  // an empty drafted group — Ember's null-customer behavior — instead of taking down the
  // whole detail page via the route-level boundary. `resetKeys` retries per delivery so a
  // failed profile on one delivery doesn't strand the fallback when navigating to the next
  // (the detail page re-renders in place rather than remounting).
  return (
    <AppErrorBoundary fallback={emptyGroup} resetKeys={[customerId]}>
      <DraftedPushGroups delivery={delivery} customerId={customerId} multiApp={multiApp} />
    </AppErrorBoundary>
  );
}

type DraftedPushGroupsProps = { delivery: Delivery; customerId: string; multiApp: boolean };

/** Reads the customer's devices (`recipientDevices`) and renders them as the drafted group. */
function DraftedPushGroups({ delivery, customerId, multiApp }: DraftedPushGroupsProps) {
  const { data: customer } = useCustomer({ customerId });
  const devices = draftedRecipientDevices(delivery, customerDevices(customer));

  return <PushGroups groups={[{ type: 'drafted', devices }]} delivery={delivery} multiApp={multiApp} />;
}

type PushGroupsProps = { groups: PushDeliveryGroup[]; delivery: Delivery; multiApp: boolean };

/**
 * Renders each group; the first is expanded by default. The app-name lookup (and
 * its apps-list read) runs only when `multi_app` is on *and* a group actually has
 * a single device — the only case the inline header renders.
 */
function PushGroups({ groups, delivery, multiApp }: PushGroupsProps) {
  const hasSingleDeviceGroup = multiApp && groups.some((group) => group.devices.length === 1);
  const apps = useAppsOptional({ enabled: hasSingleDeviceGroup });
  const appName = hasSingleDeviceGroup ? apps.data?.find((app) => app.id === delivery.app_id)?.name : undefined;
  const single: SingleDeviceOptions = { enabled: multiApp, appName };

  return (
    <>
      {groups.map((group, index) => (
        <PushDeliveryGroupSection key={group.type} group={group} defaultOpen={index === 0} single={single} />
      ))}
    </>
  );
}

type PushDeliveryGroupSectionProps = { group: PushDeliveryGroup; defaultOpen: boolean; single: SingleDeviceOptions };

/** One group: a single-device inline header (multi_app), else a titled accordion over a device table. */
function PushDeliveryGroupSection({ group, defaultOpen, single }: PushDeliveryGroupSectionProps) {
  const colors = TYPE_COLORS[group.type];

  if (single.enabled && group.devices.length === 1) {
    return <SingleDeviceGroup type={group.type} device={group.devices[0]} colors={colors} appName={single.appName} />;
  }

  const preposition = group.type === 'sent' ? 'to' : 'for';
  const summary = `${preposition} ${group.devices.length} ${plur('device', group.devices.length)}`;

  return (
    <PanelSection withBorder withPadding={false}>
      <Accordion defaultOpen={defaultOpen}>
        <AccordionTitle px="250" py="125" data-test-push-results-group-label={group.type}>
          <Text size="s" weight="regular">
            <Strong color={colors.label}>{capitalize(group.type)}</Strong> {summary}
          </Text>
        </AccordionTitle>

        <AccordionContent p="0" pb="025">
          <Table caption="List of recipient push devices" isBorderless>
            <TableThead>
              <TableTr>
                <TableTh aria-hidden h="0" p="0" style={{ width: '1%', whiteSpace: 'nowrap' }} />
                <TableTh aria-hidden h="0" p="0" style={{ maxWidth: 0 }} />
              </TableTr>
            </TableThead>

            <TableTbody>
              {group.devices.map((device, index) => (
                <DeviceRows key={`${device.id}-${index}`} device={device} messageColor={colors.message} />
              ))}
            </TableTbody>
          </Table>
        </AccordionContent>
      </Accordion>
    </PanelSection>
  );
}

type DeviceRowsProps = { device: PushDeliveryMetaReason; messageColor: TextColor };

/** A device's platform-icon + truncated id row, plus an optional failure-reason row. */
function DeviceRows({ device, messageColor }: DeviceRowsProps) {
  return (
    <>
      <TableTr data-test-device-detail>
        <TableTd data-test-device-type>
          <InlineStack alignBlock="center">
            <Icon name={platformIcon(device.platform)} color="subtle" />
          </InlineStack>
        </TableTd>
        <TableTd style={{ maxWidth: 0 }}>
          <InlineStack alignBlock="center" minW="0">
            <Truncated
              tooltip={device.id}
              family="mono"
              size="xs"
              weight="semibold"
              className="fs-mask"
              data-test-device-id
            >
              {device.id}
            </Truncated>
          </InlineStack>
        </TableTd>
      </TableTr>

      {isEmptyString(device.detail) ? null : (
        <TableTr>
          <TableTd colSpan={2} data-test-device-delivery-detail>
            <Text color={messageColor}>{formatError(device, { type: 'push' })}</Text>
          </TableTd>
        </TableTr>
      )}
    </>
  );
}

type SingleDeviceGroupProps = {
  type: PushDeliveryGroupType;
  device: PushDeliveryMetaReason;
  colors: { label?: TextColor; message?: TextColor };
  appName: string | undefined;
};

/**
 * The `multi_app` single-device group (Ember `singleDevice`): the lone device
 * rendered inline in the header — label + platform icon + id + `(app name)` —
 * instead of a "1 device" accordion, with the failure reason below if present.
 */
function SingleDeviceGroup({ type, device, colors, appName }: SingleDeviceGroupProps) {
  const preposition = type === 'sent' ? 'to' : 'for';

  return (
    <PanelSection withBorder py="150">
      <InlineStack alignBlock="center" gap="050" data-test-push-results-group-label={type}>
        <Text flexShrink="0">
          <Strong color={colors.label}>{capitalize(type)}</Strong> {preposition}
        </Text>
        <Icon name={platformIcon(device.platform)} color="subtle" flexShrink="0" />
        <Truncated tooltip={device.id} family="mono" size="s" className="fs-mask" data-test-device-id>
          {device.id}
        </Truncated>
        {isEmptyString(appName) ? null : (
          <Truncated tooltip={appName} color="subtle" size="s" data-test-device-app-name>
            ({appName})
          </Truncated>
        )}
      </InlineStack>

      {isEmptyString(device.detail) ? null : (
        <Box mt="150">
          <Text size="s" color={colors.message} data-test-device-delivery-detail>
            {formatError(device, { type: 'push' })}
          </Text>
        </Box>
      )}
    </PanelSection>
  );
}
